import { useMemo, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { ListPager, usePaged } from "@/components/ListPager";
import { toast } from "sonner";
import { Headset, History, Inbox, KeyRound, Link2, LockKeyhole, Mail, Pencil, Search, ShieldCheck, ShieldMinus, ShieldPlus, Trash2, UserPlus, UserX, UserCheck, UserMinus } from "lucide-react";
import { del, errorMessage, patch, post } from "@/lib/api";
import { TEAM_ROLE_LABELS, TEAM_ROLE_PLURALS, WORKER_ROLE_OPTIONS } from "@/lib/constants";
import { fetchLocksmiths, fetchOfficers, useAsync } from "@/lib/queries";
import { useAuth } from "@/context/AuthContext";
import { EmptyState, ErrorState, LoadingRows, PageHeader } from "@/components/States";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { UserAvatar } from "@/components/UserAvatar";
import { LocksmithDialog } from "./LocksmithDialog";
import { OfficerDialog } from "./OfficerDialog";
import { InviteLinkDialog } from "./InviteLinkDialog";
import { RemovedLocksmithsDialog } from "./RemovedLocksmithsDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

export default function Locksmiths() {
  const { data, loading, error, reload } = useAsync(() => fetchLocksmiths(), []);
  const { data: externalAgents, loading: loadingExternalAgents, error: externalAgentsError, reload: reloadExternalAgents } = useAsync(() => fetchOfficers(), []);
  const { profile } = useAuth();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedRole = searchParams.get("role");
  const enabledTypes = profile.company?.enabled_user_types ?? WORKER_ROLE_OPTIONS.map(([role]) => role);
  const selectedRole = enabledTypes.includes(requestedRole) ? requestedRole : null;
  const ownerId = profile.company?.owner_user_id;
  const isOwner = !ownerId || ownerId === profile.id;
  const [q, setQ] = useState("");
  const [dialog, setDialog] = useState({ open: false, locksmith: null });
  const [externalAgentDialog, setExternalAgentDialog] = useState({ open: false, officer: null });
  const [deletingExternalAgent, setDeletingExternalAgent] = useState(null);
  const [toggle, setToggle] = useState(null);
  const [invite, setInvite] = useState(null);
  const [promote, setPromote] = useState(null);
  const [demote, setDemote] = useState(null);
  const [support, setSupport] = useState(null);

  const deleteExternalAgent = async () => {
    try {
      await del(`/officers/${deletingExternalAgent.id}`);
      toast.success("Officer deleted");
      reloadExternalAgents();
    } catch (e) {
      toast.error(errorMessage(e));
      return false;
    }
  };

  const doSupport = async () => {
    try {
      await post(`/locksmiths/${support.id}/support-staff`, { enabled: !support.support_staff });
      toast.success(support.support_staff ? `${support.full_name || support.email} is no longer support staff` : `${support.full_name || support.email} now has site owner access`);
      reload();
    } catch (e) {
      toast.error(errorMessage(e));
      return false;
    }
    return undefined;
  };
  const [removing, setRemoving] = useState(null);
  const [enableAll, setEnableAll] = useState(false);
  const [showRemoved, setShowRemoved] = useState(false);
  const [resetting, setResetting] = useState(null);

  const doReset = async () => {
    try {
      await post(`/locksmiths/${resetting.id}/password-reset`);
      toast.success(`Password reset email sent to ${resetting.email}`);
    } catch (e) {
      toast.error(errorMessage(e));
      return false;
    }
  };
  const offCount = (data || []).filter((l) => WORKER_ROLE_OPTIONS.some(([role]) => role === l.role) && !l.open_jobs_enabled).length;

  const doEnableAll = async () => {
    try {
      const res = await post("/locksmiths/open-jobs/enable-all");
      toast.success(`Available Jobs turned on for ${res.updated} team member${res.updated === 1 ? "" : "s"}`);
      reload();
    } catch (e) {
      toast.error(errorMessage(e));
      return false;
    }
  };

  const doRemove = async () => {
    try {
      await del(`/locksmiths/${removing.id}`);
      toast.success(`${removing.full_name || removing.email} removed from your company`);
      reload();
    } catch (e) {
      toast.error(errorMessage(e));
      return false;
    }
  };

  const doDemote = async () => {
    try {
      await post(`/locksmiths/${demote.id}/demote`);
      toast.success(`${demote.full_name || demote.email} is now a locksmith`);
      reload();
    } catch (e) {
      toast.error(errorMessage(e));
      return false;
    }
  };

  const doPromote = async () => {
    try {
      await post(`/locksmiths/${promote.id}/promote`);
      toast.success(`${promote.full_name || promote.email} is now a manager`);
      reload();
    } catch (e) {
      toast.error(errorMessage(e));
      return false;
    }
  };

  const makeLink = async (l) => {
    try {
      const res = await post(`/locksmiths/${l.id}/invite-link`);
      setInvite({ name: l.full_name || l.email, link: res.invite_link });
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  const makeAcceptLink = async (l) => {
    try {
      const res = await post(`/locksmiths/${l.id}/accept-link`);
      setInvite({ link: res.accept_link, title: `Accept-invite link for ${l.full_name || l.email}`,
        description: "Send this private link (text or email). Opening it adds them to your team straight away, no sign-in needed. It expires in 7 days." });
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  const resendInvite = async (l) => {
    try {
      await post(`/locksmiths/${l.id}/resend-invite`);
      toast.success(`Invite email sent again to ${l.email}`);
      reload();
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  const rows = useMemo(() => (data || []).filter((l) => (!selectedRole || (l.user_types || [l.role]).includes(selectedRole)) &&
    [l.full_name, l.email, l.areas_covered, ...(l.postcodes || [])].join(" ").toLowerCase().includes(q.toLowerCase().trim())), [data, q, selectedRole]);
  const externalAgentRows = useMemo(() => (externalAgents || []).filter((officer) =>
    [officer.full_name, officer.company, officer.phone].join(" ").toLowerCase().includes(q.toLowerCase().trim())), [externalAgents, q]);
  const pager = usePaged(rows);

  const setActive = async () => {
    try {
      const { full_name, phone, areas_covered, postcodes } = toggle;
      await patch(`/locksmiths/${toggle.id}`, { full_name, phone, areas_covered, postcodes, active: !toggle.active });
      toast.success(toggle.active ? "Locksmith deactivated" : "Locksmith reactivated");
      reload();
    } catch (e) {
      toast.error(errorMessage(e));
      return false;
    }
  };

  return (
    <div className="space-y-6" data-testid="locksmiths-page">
      <PageHeader eyebrow="Team" title={selectedRole ? TEAM_ROLE_PLURALS[selectedRole] : "Team"} description={selectedRole ? `Manage your ${TEAM_ROLE_PLURALS[selectedRole].toLowerCase()}.` : "Manage your team members and roles."}
        actions={<div className="flex flex-wrap gap-2">
          {offCount > 0 && <Button variant="outline" onClick={() => setEnableAll(true)} data-testid="enable-open-jobs-all-button"><Inbox className="mr-1 h-4 w-4" /> Turn on Available Jobs for everyone</Button>}
          <Button variant="outline" onClick={() => setShowRemoved(true)} data-testid="add-back-removed-button"><History className="mr-1 h-4 w-4" /> Add back removed</Button>
          <Button className="bg-blue-700 hover:bg-blue-800" onClick={() => setDialog({ open: true, locksmith: null })} data-testid="invite-locksmith-button"><UserPlus className="mr-1 h-4 w-4" /> Invite team member</Button>
        </div>} />
      <Tabs value={selectedRole || "all"} onValueChange={(role) => {
        const nextParams = new URLSearchParams(searchParams);
        if (role === "all") nextParams.delete("role");
        else nextParams.set("role", role);
        setSearchParams(nextParams);
      }}>
        <TabsList className="h-auto max-w-full flex-wrap justify-start" data-testid="team-role-tabs">
          <TabsTrigger value="all" data-testid="team-role-tab-all">All team</TabsTrigger>
          {WORKER_ROLE_OPTIONS.filter(([role]) => enabledTypes.includes(role)).map(([role]) => (
            <TabsTrigger key={role} value={role} data-testid={`team-role-tab-${role}`}>{role === "agent" ? "Warrant Officer" : TEAM_ROLE_PLURALS[role]}</TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <Input placeholder="Search name, area or postcode" value={q} onChange={(e) => setQ(e.target.value)} className="bg-white pl-9" aria-label="Search locksmiths" data-testid="locksmiths-search-input" />
      </div>
      {error && <ErrorState message={error} onRetry={reload} />}
      {loading && !data && <LoadingRows />}
      {data && !rows.length && <EmptyState icon={KeyRound} title={selectedRole ? `No ${TEAM_ROLE_PLURALS[selectedRole].toLowerCase()} yet` : "No team members yet"} description={selectedRole ? `Invite your first ${TEAM_ROLE_LABELS[selectedRole]} to the team.` : "Invite your first team member to start assigning warrant runs."} testId="locksmiths-empty" />}
      {rows.length > 0 && (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
          <Table data-testid="locksmiths-table">
            <TableHeader><TableRow className="bg-slate-50">
              <TableHead>Name</TableHead><TableHead>Contact</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {pager.items.map((l) => (
                <TableRow key={l.id} data-testid={`locksmith-row-${l.id}`} className={l.active ? "" : "opacity-60"}>
                  <TableCell className="font-semibold text-slate-900">
                    <div className="flex flex-wrap items-center gap-2">
                      <UserAvatar userId={l.id} stamp={l.avatar_updated_at} name={l.full_name} className="h-9 w-9 bg-slate-100 text-slate-400" testId={`locksmith-avatar-${l.id}`} />
                      {l.full_name || "—"}
                      {(l.user_types?.length ? l.user_types : [l.role]).map((role) => <span key={role} className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${role === "manager" ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-700"}`} data-testid={role === "manager" ? `manager-badge-${l.id}` : `role-badge-${l.id}-${role}`}>{TEAM_ROLE_LABELS[role] || role}{role === "manager" && l.id === profile.id ? " · You" : ""}</span>)}
                      {l.support_staff && <span className="rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-violet-800" data-testid={`support-staff-badge-${l.id}`}>Support staff</span>}
                    </div>
                  </TableCell>
                  <TableCell className="text-sm"><div>{l.email}</div><div className="text-slate-500">{l.phone}</div></TableCell>
                  <TableCell>
                    <span className={`whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-semibold ${!l.active ? "bg-zinc-100 text-zinc-600" : l.invite_pending ? "bg-amber-50 text-amber-800" : "bg-emerald-50 text-emerald-700"}`} data-testid={`locksmith-status-${l.id}`}>{!l.active ? "Inactive" : l.membership_status === "invited" ? "Awaiting acceptance" : l.invite_pending ? "Invite pending" : "Active"}</span>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      {profile.is_primary_owner && !profile.support_view && l.role === "manager" && l.id !== profile.id && l.active && !l.invite_pending && l.membership_status === "active" && (
                        <Button size="icon" variant="ghost" aria-label={l.support_staff ? "Remove support staff access" : "Make support staff"} title={l.support_staff ? "Remove support staff access" : "Make support staff (site owner access)"}
                          className={l.support_staff ? "text-violet-700 hover:bg-violet-50" : ""} onClick={() => setSupport(l)} data-testid={`support-staff-toggle-${l.id}`}><Headset className="h-4 w-4" /></Button>
                      )}
                      {isOwner && l.role === "manager" && l.id !== profile.id && l.id !== ownerId && (
                        <Button size="icon" variant="ghost" aria-label="Make locksmith" title="Make locksmith" className="text-amber-700 hover:bg-amber-50" onClick={() => setDemote(l)} data-testid={`demote-manager-${l.id}`}><ShieldMinus className="h-4 w-4" /></Button>
                      )}
                      {isOwner && l.active && WORKER_ROLE_OPTIONS.some(([role]) => role === l.role) && l.membership_status === "active" && !l.other_companies && (
                        <Button size="icon" variant="ghost" aria-label="Make manager" title="Make manager" className="text-blue-700 hover:bg-blue-50" onClick={() => setPromote(l)} data-testid={`promote-locksmith-${l.id}`}><ShieldPlus className="h-4 w-4" /></Button>
                      )}
                      {l.id !== profile.id && l.active && (l.invite_pending || l.membership_status === "invited") && (isOwner || l.role !== "manager") && <Button size="icon" variant="ghost" aria-label="Resend invite email" title="Resend invite email" onClick={() => resendInvite(l)} data-testid={`resend-invite-${l.id}`}><Mail className="h-4 w-4" /></Button>}
                      {l.id !== profile.id && l.active && l.membership_status === "invited" && (isOwner || l.role !== "manager") && <Button size="icon" variant="ghost" aria-label="Get accept-invite link" title="Get accept-invite link" onClick={() => makeAcceptLink(l)} data-testid={`accept-link-${l.id}`}><Link2 className="h-4 w-4" /></Button>}
                      {l.id !== profile.id && l.invite_pending && (isOwner || l.role !== "manager") && <Button size="icon" variant="ghost" aria-label="Get set-password link" title="Get set-password link" onClick={() => makeLink(l)} data-testid={`link-locksmith-${l.id}`}><Link2 className="h-4 w-4" /></Button>}
                      {l.id !== profile.id && l.active && !l.invite_pending && l.membership_status === "active" && (isOwner || l.role !== "manager") && <Button size="icon" variant="ghost" aria-label="Send password reset email" title="Send password reset email" onClick={() => setResetting(l)} data-testid={`reset-password-${l.id}`}><LockKeyhole className="h-4 w-4" /></Button>}
                      {(isOwner ? true : l.role !== "manager" || l.id === profile.id) && <Button asChild size="icon" variant="ghost" aria-label="Edit team member" title="Edit team member" data-testid={`edit-locksmith-${l.id}`}>
                        <Link to={`/manager/locksmiths/${l.id}/edit`} state={{ returnTo: `${location.pathname}${location.search}` }}><Pencil className="h-4 w-4" /></Link>
                      </Button>}
                      {l.id !== profile.id && (isOwner || l.role !== "manager") && (
                      <Button size="icon" variant="ghost" aria-label={l.active ? "Deactivate" : "Reactivate"} className={l.active ? "text-rose-600 hover:bg-rose-50" : "text-emerald-600 hover:bg-emerald-50"} onClick={() => setToggle(l)} data-testid={`toggle-locksmith-${l.id}`}>
                        {l.active ? <UserX className="h-4 w-4" /> : <UserCheck className="h-4 w-4" />}
                      </Button>
                      )}
                      {l.id !== profile.id && l.id !== ownerId && (isOwner || l.role !== "manager") && (
                        <Button size="icon" variant="ghost" aria-label="Remove from company" title="Remove from company" className="text-rose-700 hover:bg-rose-50" onClick={() => setRemoving(l)} data-testid={`remove-locksmith-${l.id}`}><UserMinus className="h-4 w-4" /></Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      {rows.length > 0 && <ListPager pager={pager} label="people" testId="locksmiths-pager" />}
      {selectedRole === "agent" && <section className="space-y-3" data-testid="external-agents-section">
        <h2 className="text-lg font-semibold text-slate-900">External Agents</h2>
        {externalAgentsError && <ErrorState message={externalAgentsError} onRetry={reloadExternalAgents} />}
        {loadingExternalAgents && !externalAgents && <LoadingRows rows={3} />}
        {externalAgents && !externalAgentRows.length && <EmptyState icon={ShieldCheck} title="No external agents yet" description="Add warrant officers to make them available for agent assignments." testId="external-agents-empty" />}
        {externalAgentRows.length > 0 && <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
          <Table data-testid="external-agents-table">
            <TableHeader><TableRow className="bg-slate-50">
              <TableHead>Name</TableHead><TableHead>Company</TableHead><TableHead>Telephone</TableHead><TableHead className="text-right">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {externalAgentRows.map((officer) => (
                <TableRow key={officer.id} data-testid={`external-agent-row-${officer.id}`}>
                  <TableCell className="font-semibold text-slate-900">{officer.full_name}</TableCell>
                  <TableCell className="text-sm">{officer.company || "—"}</TableCell>
                  <TableCell className="font-data text-sm">{officer.phone ? <a href={`tel:${officer.phone}`} className="text-blue-700 hover:underline">{officer.phone}</a> : "—"}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button size="icon" variant="ghost" aria-label="Edit external agent" onClick={() => setExternalAgentDialog({ open: true, officer })} data-testid={`edit-external-agent-${officer.id}`}><Pencil className="h-4 w-4" /></Button>
                      <Button size="icon" variant="ghost" aria-label="Delete external agent" className="text-rose-600 hover:bg-rose-50" onClick={() => setDeletingExternalAgent(officer)} data-testid={`delete-external-agent-${officer.id}`}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>}
      </section>}
      <OfficerDialog open={externalAgentDialog.open} officer={externalAgentDialog.officer} onOpenChange={(open) => setExternalAgentDialog({ ...externalAgentDialog, open })} onSaved={reloadExternalAgents} />
      <ConfirmDialog open={!!deletingExternalAgent} onOpenChange={(open) => !open && setDeletingExternalAgent(null)} title="Delete external agent?"
        description={`${deletingExternalAgent?.full_name} will be permanently deleted. Officers assigned to warrant runs can't be deleted.`}
        confirmLabel="Delete officer" onConfirm={deleteExternalAgent} />
      <LocksmithDialog open={dialog.open} locksmith={dialog.locksmith} initialRole={selectedRole} onOpenChange={(o) => setDialog({ ...dialog, open: o })} onSaved={reload} onInvited={setInvite} canInviteManager={isOwner} />
      <InviteLinkDialog invite={invite} onClose={() => setInvite(null)} />
      <RemovedLocksmithsDialog open={showRemoved} onOpenChange={setShowRemoved} onRestored={reload} />
      <ConfirmDialog open={!!resetting} onOpenChange={(o) => !o && setResetting(null)} title="Send a password reset email?"
        description={`${resetting?.full_name || resetting?.email} will get an email with a link to choose a new password. The link works once and expires in 1 hour. Their current password keeps working until they change it.`}
        confirmLabel="Send reset email" onConfirm={doReset} />
      <ConfirmDialog open={!!support} onOpenChange={(o) => !o && setSupport(null)} title={support?.support_staff ? "Remove support staff access?" : "Give site owner access?"}
        description={support?.support_staff ? `${support?.full_name || support?.email} will lose site owner access (Companies, Plans, view as manager, mortise locks). They stay a manager of your company.`
          : `${support?.full_name || support?.email} will be able to do everything you can as site owner: see and edit every company, change plans and billing, view as manager, accept invites and add mortise locks. Only you can add or remove support staff.`}
        confirmLabel={support?.support_staff ? "Remove access" : "Give access"} onConfirm={doSupport} />
      <ConfirmDialog open={!!promote} onOpenChange={(o) => !o && setPromote(null)} title="Make this locksmith a manager?"
        description={`${promote?.full_name || promote?.email} will get full manager access: all warrant runs, locksmiths, officers and lock change reports, including key safe codes. They'll be signed out and must sign in again. Their existing runs stay assigned to them.`}
        confirmLabel="Make manager" onConfirm={doPromote} />
      <ConfirmDialog open={!!demote} onOpenChange={(o) => !o && setDemote(null)} title="Change this manager to a locksmith?"
        description={`${demote?.full_name || demote?.email} will lose manager access and will only see warrant runs assigned to them. They'll be signed out and must sign in again.`}
        confirmLabel="Make locksmith" onConfirm={doDemote} />
      <ConfirmDialog open={!!toggle} onOpenChange={(o) => !o && setToggle(null)}
        title={toggle?.active ? "Deactivate locksmith?" : "Reactivate locksmith?"}
        description={toggle?.active ? `${toggle?.full_name || toggle?.email} will lose access to the app and their runs. You can reactivate them later.` : `${toggle?.full_name || toggle?.email} will regain access to their assigned runs.`}
        confirmLabel={toggle?.active ? "Deactivate" : "Reactivate"} onConfirm={setActive} />
      <ConfirmDialog open={enableAll} onOpenChange={setEnableAll} title="Turn on Available Jobs for everyone?"
        description={`${offCount} locksmith${offCount === 1 ? "" : "s"} will get the Available Jobs tab, can accept open runs and will get open job emails. You can still turn it off for anyone in their profile.`}
        confirmLabel="Turn on for everyone" onConfirm={doEnableAll} />
      <ConfirmDialog open={!!removing} onOpenChange={(o) => !o && setRemoving(null)} title="Remove from your company?"
        description={`${removing?.full_name || removing?.email} will no longer see your company's runs. Their account and any other companies they work for aren't affected. Existing runs stay as they are.`}
        confirmLabel="Remove" destructive onConfirm={doRemove} />
    </div>
  );
}
